import { Link } from 'react-router-dom'
import { PublicLayout } from '../components/layout/PublicLayout'

const LAST_UPDATED = 'June 1, 2026'

export default function PrivacyPage() {
  return (
    <PublicLayout>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 80px' }}>
        <div style={{ marginBottom: 40 }}>
          <p style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, margin: '0 0 10px' }}>
            Legal
          </p>
          <h1 style={{ fontSize: 36, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 10px' }}>
            Privacy Policy
          </h1>
          <p style={{ fontSize: 14, color: 'var(--muted)', margin: 0 }}>Last updated {LAST_UPDATED}</p>
        </div>

        <div style={{ fontSize: 15, lineHeight: 1.7, color: 'var(--ink-2)', display: 'flex', flexDirection: 'column', gap: 32 }}>
          <p>
            Fellowship ("we", "us", or "our") operates a community events platform for Christian churches and their congregations. This policy explains what information we collect, how we use it, and the choices you have.
          </p>

          <Section title="Information we collect">
            <p><strong>Account information.</strong> When you register, we collect your name, email address, and password (stored as a secure hash). You may also connect a Google account for sign-in.</p>
            <p><strong>Church profile information.</strong> Church administrators provide their church name, address, denomination, service times, website, and giving URL. This information is displayed publicly on the church's Fellowship profile.</p>
            <p><strong>Event information.</strong> Hosts create events with titles, descriptions, addresses, dates, and dish goals. Church events are public by default. Person-hosted events may be public, unlisted, or invite-only per the host's choice.</p>
            <p><strong>RSVP information.</strong> When you RSVP to an event, we record your attendance status, dish contribution, and any notes you provide. Event hosts can see who has RSVPed and their responses.</p>
            <p><strong>Location data.</strong> When you search for events, we use your entered location or browser-provided coordinates to find nearby gatherings. We do not store your device location.</p>
            <p><strong>Payment information.</strong> Church subscriptions are processed by Stripe. We do not store credit card numbers on our servers. We receive billing status and invoice history from Stripe.</p>
            <p><strong>Usage data.</strong> We collect standard server logs including IP addresses, browser type, and pages visited to operate and improve the service.</p>
          </Section>

          <Section title="How we use your information">
            <ul>
              <li>To provide and operate the Fellowship platform</li>
              <li>To send event reminders, RSVP confirmations, and broadcast messages from event hosts</li>
              <li>To notify you of new events from churches you follow</li>
              <li>To process church subscription payments through Stripe</li>
              <li>To display your name to event hosts and co-attendees when you RSVP</li>
              <li>To detect and prevent fraud, abuse, or violations of our Terms of Service</li>
              <li>To improve the platform based on aggregate usage patterns</li>
            </ul>
          </Section>

          <Section title="Information sharing">
            <p><strong>With event hosts.</strong> When you RSVP to an event, the host (and their designated coordinators) can see your name, email, attendance status, and dish contribution. For events that hide their location, your exact location is only shared after the host approves your RSVP.</p>
            <p><strong>With other attendees.</strong> Your name and dish contribution may be visible to other confirmed attendees depending on the event's settings.</p>
            <p><strong>With service providers.</strong> We share data with Stripe (payment processing), Resend (transactional email), Mapbox (maps and geocoding), and our cloud infrastructure providers. These providers process data only as needed to provide their services.</p>
            <p><strong>We do not sell your personal information.</strong> We do not share your data with advertisers or data brokers.</p>
            <p><strong>Legal requirements.</strong> We may disclose information if required by law or to protect the rights, property, or safety of Fellowship, our users, or others.</p>
          </Section>

          <Section title="Address privacy">
            <p>When a host hides an event's location, the exact address and precise map pin are never shown publicly — we display only the general area (a soft circle on the map) until the host approves your RSVP, then the exact location is revealed. Private-home events are hidden by default. Church-hosted events at the church building show the full address, since it is the church's own public venue; a church can still hide an off-site location (like a member's home) until approval.</p>
          </Section>

          <Section title="Data retention">
            <p>We retain your account information for as long as your account is active. If you delete your account, we remove your personal profile and anonymize your historical RSVP records within 30 days. Church subscription records are retained for seven years for accounting purposes.</p>
          </Section>

          <Section title="Your choices">
            <ul>
              <li><strong>Account deletion.</strong> You can delete your account from your profile settings at any time.</li>
              <li><strong>Email notifications.</strong> You can manage email preferences in your profile settings.</li>
              <li><strong>Church data.</strong> Church administrators can edit or delete their church profile and events from the dashboard.</li>
            </ul>
          </Section>

          <Section title="Cookies">
            <p>We use cookies and local storage to keep you signed in and remember preferences. We do not use third-party advertising cookies.</p>
          </Section>

          <Section title="Children's privacy">
            <p>Fellowship is not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, please contact us and we will delete it.</p>
          </Section>

          <Section title="Changes to this policy">
            <p>We may update this policy from time to time. We will notify registered users by email before material changes take effect. Continued use of the service after changes constitutes acceptance.</p>
          </Section>

          <Section title="Contact">
            <p>Questions about this policy? Email us at <a href="mailto:privacy@davenportministries.com" style={{ color: 'var(--primary)' }}>privacy@davenportministries.com</a>.</p>
          </Section>

          <p style={{ fontSize: 13, color: 'var(--muted)', paddingTop: 16, borderTop: '1px solid var(--line-soft)' }}>
            <Link to="/terms" style={{ color: 'var(--primary)' }}>Terms of Service</Link> · <Link to="/privacy" style={{ color: 'var(--primary)' }}>Privacy Policy</Link>
          </p>
        </div>
      </div>
    </PublicLayout>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', margin: '0 0 12px', color: 'var(--ink)' }}>
        {title}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {children}
      </div>
    </div>
  )
}
